import { css } from "@emotion/css";
import { space, text, themeTokens, borderRadius } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import { Button } from "../Button";
import { DeprecatedIconButton } from "../IconButton";
import { StepCount } from "../StepCount";
import type { PopoverProps } from "./Popover";
import { Popover } from "./Popover";
import { PopoverContentContainer } from "./PopoverContentContainer";
import type { PopoverWidth } from "./PopoverContentContainer";
export interface PopoverSequentialProps extends PopoverProps {
    title: string;
    image?: string;
    video?: string;
    description: ReactNode;
    infoCallout?: ReactNode;
    currentStep: number;
    totalSteps: number;
    onBack: () => void;
    onNext: () => void;
    onFinish: () => void;
    width?: PopoverWidth;
}
export function PopoverSequential({ width = "wide", ...props }: PopoverSequentialProps): JSX.Element {
    return (<Popover placement={props.placement} open={props.open} anchorEl={props.anchorEl} onClose={props.onClose}>
            <PopoverContentContainer width={width}>
                <div className={styles.titleContainer}>
                    <div className={styles.title}>{props.title}</div>
                    <div>
                        <DeprecatedIconButton accessibleName="ClosePopover" icon={"XmarkIcon"} onClick={props.onClose}/>
                    </div>
                </div>
                {props.image && <img className={styles.image} src={props.image}/>}
                {props.video && (<p>
                        <video src={props.video} playsInline={true} autoPlay={true} loop={true} muted={true} className={styles.video}/>
                    </p>)}
                <div className={styles.content}>{props.description}</div>
                {props.infoCallout && <div className={styles.tipContainer}>{props.infoCallout}</div>}
                <div className={styles.bottomContainer}>
                    <StepCount variant="text" currentStep={props.currentStep} totalSteps={props.totalSteps}/>
                    <div className={styles.buttonsContainer}>
                        {props.currentStep > 1 && <Button label={"Back"} importance={"secondary"} onClick={props.onBack}></Button>}
                        {props.currentStep < props.totalSteps && <Button label={"Next"} importance={"primary"} onClick={props.onNext}></Button>}
                        {props.currentStep >= props.totalSteps && <Button label={"Finish"} importance={"primary"} onClick={props.onFinish}></Button>}
                    </div>
                </div>
            </PopoverContentContainer>
        </Popover>);
}
const styles = {
    titleContainer: css({
        display: "flex",
        alignItems: "start",
        justifyContent: "space-between",
    }),
    title: css({
        font: text.body.bold.large,
        paddingRight: space[8],
    }),
    content: css({
        marginTop: space[12],
        marginBottom: space[12],
        font: text.body.regular.medium,
    }),
    image: css({
        width: "100%",
        paddingTop: space[12],
    }),
    tipContainer: css({
        borderRadius: borderRadius.small,
        padding: space[12],
        marginBottom: space[12],
        font: text.body.regular.medium,
        backgroundColor: themeTokens.color.popover.background.secondary,
    }),
    bottomContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    }),
    buttonsContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        justifyContent: "center",
    }),
    video: css({
        width: "100%",
        borderRadius: borderRadius.small,
        paddingTop: space[12],
    }),
};
